import { Button, type ButtonVariant } from '@heidi/design-system/button';
import { Textarea } from '@heidi/design-system/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyFootNote, TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ArrowUp, ArrowUpToLine, Check, Pencil, Trash2, X } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useIntl } from 'react-intl';

import { queuedMessageListMessages } from './QueuedMessageList.messages';

interface QueuedRowActionProps {
  label: string;
  tooltip?: string;
  onClick: () => void;
  variant?: ButtonVariant;
  children: ReactNode;
}

const QueuedRowAction = ({
  label,
  tooltip = label,
  onClick,
  variant = 'ghost',
  children,
}: QueuedRowActionProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        type="button"
        variant={variant}
        size="xs"
        iconOnly
        onClick={onClick}
        aria-label={label}
      >
        {children}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{tooltip}</TooltipContent>
  </Tooltip>
);

interface QueuedMessageRowProps {
  id: string;
  text: string;
  secondaryText?: string;
  editableText?: string;
  index: number;
  onSendNow: (id: string) => void;
  onRemove: (id: string) => void;
  onEdit?: (id: string, content: string) => void;
  onMoveToFront?: (id: string) => void;
  /** Fires when this row enters/leaves inline edit. */
  onEditingChange?: (id: string, isEditing: boolean) => void;
}

export const QueuedMessageRow = ({
  id,
  text,
  secondaryText,
  editableText,
  index,
  onSendNow,
  onRemove,
  onEdit,
  onMoveToFront,
  onEditingChange,
}: QueuedMessageRowProps) => {
  const intl = useIntl();
  const canEdit = onEdit !== undefined && editableText !== undefined;
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(editableText ?? '');
  const isEditingRef = useRef(false);
  isEditingRef.current = isEditing;

  // Keep the latest callback without putting it in the cleanup dep list —
  // syncWorld-backed parents recreate onEditingChange on ordinary prop churn,
  // which must not release an active editor hold.
  const onEditingChangeRef = useRef(onEditingChange);
  onEditingChangeRef.current = onEditingChange;

  useEffect(() => {
    return () => {
      if (isEditingRef.current) {
        onEditingChangeRef.current?.(id, false);
      }
    };
  }, [id]);

  const handleStartEdit = useCallback(() => {
    setEditContent(editableText ?? '');
    setIsEditing(true);
    onEditingChange?.(id, true);
  }, [editableText, id, onEditingChange]);

  const handleSaveEdit = useCallback(() => {
    const trimmed = editContent.trim();
    if (trimmed && trimmed !== editableText?.trim()) {
      onEdit?.(id, trimmed);
    }
    setIsEditing(false);
    onEditingChange?.(id, false);
  }, [editContent, editableText, id, onEdit, onEditingChange]);

  const handleCancelEdit = useCallback(() => {
    setEditContent(editableText ?? '');
    setIsEditing(false);
    onEditingChange?.(id, false);
  }, [editableText, id, onEditingChange]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleCancelEdit();
        return;
      }
      // Enter saves; Shift+Enter inserts a newline (pre-PR multiline semantics).
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSaveEdit();
      }
    },
    [handleCancelEdit, handleSaveEdit]
  );

  return (
    <div className="group/queued flex shrink-0 items-start gap-2 p-1">
      {isEditing && canEdit ? (
        <>
          <Textarea
            value={editContent}
            onChange={(event) => setEditContent(event.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            autoResize
            minRows={1}
            maxRows={8}
            className={cn(
              'min-h-0 min-w-0 flex-1 resize-none border-0 bg-transparent px-0 py-0.5 shadow-none',
              'text-text-secondary caret-text-primary'
            )}
            aria-label={intl.formatMessage(queuedMessageListMessages.edit)}
          />
          <div className="flex shrink-0 flex-row items-center gap-0.5 pt-0.5">
            <QueuedRowAction
              label={intl.formatMessage(queuedMessageListMessages.save)}
              onClick={handleSaveEdit}
            >
              <Check />
            </QueuedRowAction>
            <QueuedRowAction
              label={intl.formatMessage(queuedMessageListMessages.cancel)}
              onClick={handleCancelEdit}
            >
              <X />
            </QueuedRowAction>
          </div>
        </>
      ) : (
        <>
          {/*
            Actions stay in-flow (shrink-0) so narrow widths / localized labels
            and 3–4 icon buttons never cover the preview. Opacity only dims
            them until hover/focus-within — space remains reserved.
          */}
          <div className="flex min-w-0 flex-1 flex-col">
            <TypographyP2 className="m-0 truncate text-text-secondary transition-colors group-hover/queued:text-text-primary">
              {text}
            </TypographyP2>
            {secondaryText ? (
              <TypographyFootNote className="m-0 truncate text-text-tertiary transition-colors group-hover/queued:text-text-secondary">
                {secondaryText}
              </TypographyFootNote>
            ) : null}
          </div>
          <div
            className={cn(
              'flex shrink-0 items-center gap-0.5 pt-0.5',
              'opacity-100 md:opacity-0',
              'motion-safe:transition-opacity motion-safe:duration-200 motion-safe:ease-out',
              'motion-reduce:transition-none',
              'md:group-focus-within/queued:opacity-100 md:group-hover/queued:opacity-100'
            )}
          >
            <QueuedRowAction
              label={intl.formatMessage(queuedMessageListMessages.sendNowAria)}
              tooltip={intl.formatMessage(queuedMessageListMessages.sendNow)}
              onClick={() => onSendNow(id)}
            >
              <ArrowUp />
            </QueuedRowAction>
            {onMoveToFront && index > 0 ? (
              <QueuedRowAction
                label={intl.formatMessage(queuedMessageListMessages.moveUp)}
                tooltip={intl.formatMessage(queuedMessageListMessages.moveUpTooltip)}
                onClick={() => onMoveToFront(id)}
              >
                <ArrowUpToLine />
              </QueuedRowAction>
            ) : null}
            {canEdit ? (
              <QueuedRowAction
                label={intl.formatMessage(queuedMessageListMessages.edit)}
                tooltip={intl.formatMessage(queuedMessageListMessages.editTooltip)}
                onClick={handleStartEdit}
              >
                <Pencil />
              </QueuedRowAction>
            ) : null}
            <QueuedRowAction
              label={intl.formatMessage(queuedMessageListMessages.removeFromQueueAria)}
              tooltip={intl.formatMessage(queuedMessageListMessages.remove)}
              onClick={() => onRemove(id)}
              variant="destructive_ghost"
            >
              <Trash2 />
            </QueuedRowAction>
          </div>
        </>
      )}
    </div>
  );
};
